import { useState, useCallback } from 'react';
import { pdf } from '@react-pdf/renderer';
import MurphyReport from './MurphyReport';
import mapToReport from './mapToReport';

const sanitizeFilename = (s) => String(s || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '')
  .slice(0, 60);

const todayStamp = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revocation slightly so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * Browser-side hook for rendering a Murphy report PDF and triggering a
 * download. Wraps the @react-pdf renderer + the data mapper.
 */
export default function useDownloadMurphyReport() {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const download = useCallback(async ({ meeting, assessment, variant = 'internal' }) => {
    setGenerating(true);
    setError(null);
    try {
      const report = mapToReport({ meeting, assessment, variant });
      const blob = await pdf(<MurphyReport report={report} />).toBlob();
      const slug = sanitizeFilename(meeting?.meetingTitle || 'murphy-report');
      const filename = `Murphy-Report__${slug}__${variant}__${todayStamp()}.pdf`;
      triggerDownload(blob, filename);
    } catch (err) {
      console.error('Murphy report download failed:', err);
      setError(err?.message || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  }, []);

  return { download, generating, error };
}
